import {
  forceLinting,
  forEachDiagnostic,
  setDiagnosticsEffect,
} from '@codemirror/lint';
import type { EditorState, Extension } from '@codemirror/state';
import { EditorView, type ViewUpdate } from '@codemirror/view';
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { useInternalFormRegistration } from '../../forms/form/hooks/useInternalFormRegistration.js';
import { useFormFieldMessagesContext } from '../../forms/forms-core/hooks/useFormFieldMessagesContext.js';
import { FormFieldMessagesAction } from '../../forms/forms-core/state/formfield-messages-state-reducer.js';
import type { MessageTarget } from '../../forms/validation/message-routing.js';
import {
  NativeValidityStateKeys,
  StratoValidityStateKeys,
  ValidationError,
  type ValidationState,
  type ValidationStatus,
  type ValidationTrigger,
} from '../../forms/validation/types.js';
import { useValidationStateEmitter } from '../../forms/validation/useValidationStateEmitter.js';
import { withSource } from '../../forms/validation/validation-builders.js';
import type { InlineDiagnostic } from '../code-editor/inline-diagnostic.js';

type BuildValidationState = (
  trigger: ValidationTrigger,
  status?: ValidationStatus,
) => ValidationState;

/**
 * Mutable state of the async lint lifecycle. Held in a single ref so the
 * CodeMirror listener — created once for the editor's lifetime — always reads
 * current values without being recreated.
 */
type LintState = {
  /**
   * A lint run started by a doc change is in flight.
   */
  isPending: boolean;
  /**
   *  A lint run has completed at least once, confirming a linter is installed.
   */
  hasLinter: boolean;
  /**
   * Blur happened mid-run; the settled emission should use the 'blur' trigger.
   */
  pendingBlur: boolean;
  /**
   * A first run is always scheduled at editor creation, so diagnostics for the
   * initial value are unknown until it settles.
   */
  hasInitialSettled: boolean;
  /**
   * Callers awaiting settlement. A list so overlapping calls all resolve.
   */
  waiters: Array<() => void>;
  /**
   * Latest diagnostics reported by the built-in linter.
   */
  builtinDiagnostics: Array<{ severity?: string }>;
};

const createLintState = (): LintState => ({
  isPending: false,
  hasLinter: false,
  pendingBlur: false,
  hasInitialSettled: false,
  waiters: [],
  builtinDiagnostics: [],
});

const resolveWaiters = (lint: LintState) => {
  const pending = lint.waiters;
  lint.waiters = [];
  for (const resolve of pending) {
    resolve();
  }
};

const hasSettledDiagnostics = (update: ViewUpdate) =>
  update.transactions.some((tr) =>
    tr.effects.some((e) => e.is(setDiagnosticsEffect)),
  );

const snapshotDiagnostics = (state: EditorState) => {
  const diagnostics: Array<{ severity?: string }> = [];
  forEachDiagnostic(state, (d) => {
    diagnostics.push({ severity: d.severity });
  });
  return diagnostics;
};

/**
 * Everything the update listener needs. Passed explicitly so the listener can be
 * built once, outside the render cycle, without capturing per-render closures.
 */
type ListenerContext = {
  lintRef: RefObject<LintState>;
  emitRef: RefObject<(state: ValidationState) => void>;
  buildValidationRef: RefObject<BuildValidationState>;
  isDiagnosticsEnabledRef: RefObject<boolean>;
  notifyBlur: () => void;
};

const handleDocChanged = (
  { lintRef, emitRef, buildValidationRef }: ListenerContext,
  state: EditorState,
) => {
  const lint = lintRef.current;
  // New input cancels a deferred blur — the user is editing again.
  lint.pendingBlur = false;

  // No linter, or an empty doc that only yields valueMissing — the lint result
  // cannot change the verdict, so settle instead of waiting out the debounce.
  if (!lint.hasLinter || state.doc.toString().trim() === '') {
    lint.isPending = false;
    emitRef.current(buildValidationRef.current('change', 'done'));
    return;
  }

  lint.isPending = true;
  emitRef.current(buildValidationRef.current('change', 'pending'));
};

const handleLintSettled = (ctx: ListenerContext, state: EditorState) => {
  const { lintRef, emitRef, buildValidationRef, isDiagnosticsEnabledRef } = ctx;
  const lint = lintRef.current;

  lint.hasLinter = true;
  lint.isPending = false;
  lint.hasInitialSettled = true;

  if (!isDiagnosticsEnabledRef.current) {
    lint.builtinDiagnostics = snapshotDiagnostics(state);
  }

  const settleTrigger = lint.pendingBlur ? 'blur' : 'change';
  lint.pendingBlur = false;
  emitRef.current(buildValidationRef.current(settleTrigger, 'done'));

  resolveWaiters(lint);
};

const createValidationListener = (ctx: ListenerContext): Extension =>
  EditorView.updateListener.of((update) => {
    if (update.focusChanged) {
      if (update.view.hasFocus) {
        // Re-focus cancels a deferred blur — the user is editing again.
        ctx.lintRef.current.pendingBlur = false;
      } else {
        // CodeMirror's focusChanged tracks .cm-content, where focus sits for
        // mouse users, so it is the reliable signal that the editor lost focus.
        ctx.notifyBlur();
      }
    }

    if (update.docChanged && !ctx.isDiagnosticsEnabledRef.current) {
      handleDocChanged(ctx, update.state);
    }

    if (hasSettledDiagnostics(update)) {
      handleLintSettled(ctx, update.state);
    }
  });

/**
 * Options for {@link useEditorValidation}.
 */
type UseEditorValidationOptions = {
  /**
   * Ref to the live `EditorView` instance.
   */
  viewRef: RefObject<EditorView>;
  /**
   * Consumer-supplied diagnostics, used when they override the built-in linter.
   */
  diagnosticsRef: RefObject<InlineDiagnostic[]>;
  /**
   * Whether consumer diagnostics are in use, which makes validation synchronous.
   */
  isDiagnosticsEnabledRef: RefObject<boolean>;
  /**
   * The `id` prop forwarded from the host component, used for form association.
   */
  id?: string;
  /**
   * The `name` attribute for form submission.
   */
  name?: string;
  /**
   * Whether the field is required; triggers a `valueMissing` error when empty.
   */
  required?: boolean;
  /**
   * Associates the control with a `<Form>` by its id.
   */
  form?: string;
  /**
   * Controls where Strato validation messages are routed.
   */
  messageTarget?: MessageTarget | MessageTarget[];
  /**
   * Callback fired when the editor's validation state changes.
   */
  onValidityChange?: (state: ValidationState) => void;
  /**
   * Reveals an error immediately, bypassing the blur gate.
   */
  markTouched?: () => void;
  /**
   * The current consumer-supplied `diagnostics` prop value; re-emits validation
   * when it changes (synchronous path only).
   */
  diagnostics?: InlineDiagnostic[];
  /**
   * The current editor `value` prop; re-emits validation when it changes
   * (synchronous path only).
   */
  value?: string;
};

/**
 * Result of {@link useEditorValidation}.
 */
type UseEditorValidationResult = {
  /** CodeMirror extension driving the validation state machine. */
  validationListener: Extension;
  /**
   * Validates on demand, awaiting any outstanding lint run first. `reveal`
   * surfaces a resulting error even while focus is inside.
   */
  validate: (
    trigger: ValidationTrigger,
    reveal?: boolean,
  ) => Promise<ValidationState>;
  /** Reports a blur that CodeMirror's own focus tracking did not observe. */
  notifyBlur: () => void;
};

/**
 * Drives the editor's validation lifecycle: tracks async lint runs, emits
 * `ValidationState` transitions, and registers the editor with an enclosing
 * `Form` so it participates in submission and message routing.
 *
 * @internal
 */
export function useEditorValidation({
  viewRef,
  diagnosticsRef,
  isDiagnosticsEnabledRef,
  id,
  name,
  required,
  form,
  messageTarget,
  onValidityChange,
  markTouched,
  diagnostics,
  value,
}: UseEditorValidationOptions): UseEditorValidationResult {
  const lintRef = useRef<LintState>(createLintState());

  const markTouchedRef = useRef(markTouched);
  useLayoutEffect(() => {
    markTouchedRef.current = markTouched;
  });

  const requiredRef = useRef(required);
  useEffect(() => {
    requiredRef.current = required;
  }, [required]);

  // Assigned from the hook calls below, so the listener reaches them indirectly.
  const emitRef = useRef<(state: ValidationState) => void>(
    (_state) => undefined,
  );
  const buildValidationRef = useRef<BuildValidationState>(
    (trigger, status) => ({
      status: status ?? 'done',
      trigger,
      errors: [],
    }),
  );

  const buildValidationState = useCallback<BuildValidationState>(
    (trigger, status = 'done') => {
      const val = viewRef.current.state.doc.toString();
      const errors: ValidationError[] = [];

      if (requiredRef.current && val.trim() === '') {
        errors.push(
          new ValidationError('Value is required.', {
            source: 'strato',
            validityStateKey: NativeValidityStateKeys.valueMissing,
          }),
        );
      } else if (val.trim() !== '') {
        const activeDiagnostics = isDiagnosticsEnabledRef.current
          ? diagnosticsRef.current
          : lintRef.current.builtinDiagnostics;
        const hasErrors = activeDiagnostics.some(
          (d) => (d.severity ?? 'error') === 'error',
        );
        if (hasErrors) {
          errors.push(
            new ValidationError('The editor contains syntax errors.', {
              source: isDiagnosticsEnabledRef.current ? 'custom' : 'strato',
              validityStateKey: StratoValidityStateKeys.syntaxError,
            }),
          );
        }
      }

      return { status, trigger, errors };
    },
    [viewRef, diagnosticsRef, isDiagnosticsEnabledRef],
  );
  useLayoutEffect(() => {
    buildValidationRef.current = buildValidationState;
  });

  // Emits a blur state, or defers it until the in-flight run settles so the
  // emitted state reflects the actual lint result rather than stale diagnostics.
  const notifyBlur = useCallback(() => {
    const lint = lintRef.current;
    if (lint.isPending) {
      lint.pendingBlur = true;
    } else {
      emitRef.current(buildValidationRef.current('blur'));
    }
  }, []);

  // Resolves once no lint run is outstanding, so validation never reports on
  // stale diagnostics. `isPending` only covers runs started by a doc change,
  // hence the separate initial-run check.
  const awaitLintSettled = useCallback(() => {
    const lint = lintRef.current;
    if (isDiagnosticsEnabledRef.current) {
      return Promise.resolve();
    }
    if (lint.hasInitialSettled && !lint.isPending) {
      return Promise.resolve();
    }
    // Registered before forceLinting so a synchronous linter settling inline finds it.
    return new Promise<void>((resolve) => {
      lint.waiters.push(resolve);
      forceLinting(viewRef.current);
    });
  }, [viewRef, isDiagnosticsEnabledRef]);

  const validate = useCallback(
    async (trigger: ValidationTrigger, reveal = false) => {
      await awaitLintSettled();
      const state = buildValidationRef.current(trigger);
      // Reveal before emitting so both land in the same render.
      if (reveal) {
        markTouchedRef.current?.();
      }
      emitRef.current(state);
      return state;
    },
    [awaitLintSettled],
  );
  const validateRef = useRef(validate);
  useLayoutEffect(() => {
    validateRef.current = validate;
  });

  const listenerRef = useRef<Extension | null>(null);
  const validationListener = (listenerRef.current ??= createValidationListener({
    lintRef,
    emitRef,
    buildValidationRef,
    isDiagnosticsEnabledRef,
    notifyBlur,
  }));

  // Release in-flight callers on unmount; no further lint run will settle.
  useEffect(() => {
    const lint = lintRef.current;
    return () => resolveWaiters(lint);
  }, []);

  const { dispatch: dispatchFieldMessages } = useFormFieldMessagesContext();

  // Tracks which error message IDs we have dispatched into FormFieldMessagesContext
  // so we can remove them when errors clear or the field resets.
  const addedMessageIdsRef = useRef<Set<string>>(new Set());

  const clearFieldMessages = useCallback(() => {
    for (const messageId of addedMessageIdsRef.current) {
      dispatchFieldMessages?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: { id: messageId, source: 'strato' },
      });
    }
    addedMessageIdsRef.current.clear();
  }, [dispatchFieldMessages]);

  const { onValidityChange: reportValidity } = useInternalFormRegistration({
    id,
    name,
    form,
    messageTarget,
    getValue: () => viewRef.current.state.doc.toString(),
    focus: () => viewRef.current.focus(),
    onValidityChange,
    validateField: ({ reveal, trigger }) =>
      validateRef.current(trigger, reveal),
    resetValidation: () => {
      clearFieldMessages();
      dispatchFieldMessages?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: false, resetShowError: true },
      });
      emitRef.current({ status: 'done', trigger: 'initial', errors: [] });
    },
  });

  // Bridge: forward settled done-state emissions into FormFieldMessagesContext so
  // FormFieldMessages can render the text and useFormControl's showError activates.
  const trackedReportValidity = useCallback(
    (state: ValidationState) => {
      if (state.status === 'done') {
        // Add before removing so `showError` survives an error swap.
        const nextMessageIds = new Set<string>();
        for (const error of state.errors) {
          const messageId = error.validityStateKey.toString();
          dispatchFieldMessages?.({
            type: FormFieldMessagesAction.ADDMESSAGE,
            payload: {
              id: messageId,
              error: withSource(error, 'strato'),
            },
          });
          nextMessageIds.add(messageId);
        }
        for (const messageId of addedMessageIdsRef.current) {
          if (!nextMessageIds.has(messageId)) {
            dispatchFieldMessages?.({
              type: FormFieldMessagesAction.REMOVEMESSAGE,
              payload: { id: messageId, source: 'strato' },
            });
          }
        }
        addedMessageIdsRef.current = nextMessageIds;

        // Mark as touched on any non-change trigger (blur, submit, manual).
        // Change-triggered done states only update messages; isTouched gates display.
        if (state.trigger !== 'change' && state.trigger !== 'initial') {
          dispatchFieldMessages?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
        }
      }
      reportValidity(state);
    },
    [reportValidity, dispatchFieldMessages],
  );

  const emit = useValidationStateEmitter(trackedReportValidity);
  useLayoutEffect(() => {
    emitRef.current = emit;
  });

  // Emit when consumer-supplied diagnostics change (synchronous path).
  useEffect(() => {
    if (!isDiagnosticsEnabledRef.current) {
      return;
    }
    emitRef.current(buildValidationRef.current('change', 'done'));
  }, [diagnostics, value, isDiagnosticsEnabledRef]);

  return {
    validationListener,
    validate,
    notifyBlur,
  };
}
